import {
    Box,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextareaAutosize,
    Typography
} from '@mui/material';
import React, { useState, Dispatch } from 'react';
import { PhysicianReviewModel } from '../../lib/models/PhysicianReviewModel';
import t from '../../theme';
import {
    headerRowStyle,
    radioRowStyle,
    grayedRowStyle,
    reasonTextStyle,
    borderRightStyle,
    lastGrayedRowStyle,
    firstHeaderRowStyle,
    headerRowStyleFlex
} from '../../style';
import { useScreenSmallerThan } from '../../lib/hooks/breakpoint-hooks';
import { FlexCenterBox } from '../../styledComponents';

interface PhysicianReviewProps {
    physicianReview: PhysicianReviewModel | undefined;
    label: string;
    setAnyChangeMade: Dispatch<boolean>;
}

const PhysicianReviewFirstPart = ({
    physicianReview,
    label,
    setAnyChangeMade
}: PhysicianReviewProps) => {
    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const isSmallerThanMedium = useScreenSmallerThan('md');

    const [isNewInclusionCriteria, setIsNewInclusionCriteria] = useState(
        physicianReview?.isInclusionCriteriaMet != null ? false : true
    );
    const [isNewExclusionCriteria, setIsNewExclusionCriteria] = useState(
        physicianReview?.isExclusionCriteriaPresent != null ? false : true
    );
    const [isNewServiceAppropiate, setIsNewServiceAppropiate] = useState(
        physicianReview?.isServiceAppropiate != null ? false : true
    );
    const [isNewAllInformation, setIsNewAllInformation] = useState(
        physicianReview?.haveAllInformation != null ? false : true
    );
    const [isMetValue, setIsMetValue] = useState(
        isNewInclusionCriteria ? undefined : physicianReview?.isInclusionCriteriaMet ?? false
    );
    const [reasonInclusionCriteriaValue, setReasonInclusionCriteriaValue] = useState(
        physicianReview?.reasonInclusionCriteria ?? ''
    );
    const [isExclusionCriteriaValue, setIsExclusionCriteriaValue] = useState(
        isNewExclusionCriteria ? undefined : physicianReview?.isExclusionCriteriaPresent ?? false
    );
    const [listExclusionCriteriaValue, setListExclusionCriteriaValue] = useState(
        physicianReview?.listExclusionCriteria ?? ''
    );
    const [isServiceAppropiateValue, setIsServiceAppropiateValue] = useState(
        isNewServiceAppropiate ? undefined : physicianReview?.isServiceAppropiate ?? false
    );
    const [reasonServiceAppropiateValue, setReasonServiceAppropiateValue] = useState(
        physicianReview?.reasonServiceAppropiate ?? ''
    );
    const [haveAllInformationValue, setHaveAllInformationValue] = useState(
        isNewAllInformation ? undefined : physicianReview?.haveAllInformation ?? false
    );
    const [reasonDontHaveAllInformationValue, setReasonDontHaveAllInformationValue] = useState(
        physicianReview?.reasonDontHaveAllInformation ?? ''
    );
    const [isReviewedValue, setIsReviewedValue] = useState(physicianReview?.isReviewed ?? false);

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const handleReasonInclusionCriteriaChange = (event: any) => {
        setAnyChangeMade(true);
        if (event.nativeEvent.inputType === 'insertLineBreak') {
            setReasonInclusionCriteriaValue(reasonInclusionCriteriaValue);
        } else {
            setReasonInclusionCriteriaValue(event.target.value);
        }
    };

    const handleListExclusionCriteriaChange = (event: any) => {
        setAnyChangeMade(true);
        if (event.nativeEvent.inputType === 'insertLineBreak') {
            setListExclusionCriteriaValue(listExclusionCriteriaValue);
        } else {
            setListExclusionCriteriaValue(event.target.value);
        }
    };

    const handleReasonServiceAppropiateChange = (event: any) => {
        setAnyChangeMade(true);
        if (event.nativeEvent.inputType === 'insertLineBreak') {
            setReasonServiceAppropiateValue(reasonServiceAppropiateValue);
        } else {
            setReasonServiceAppropiateValue(event.target.value);
        }
    };

    const handleReasonDontHaveAllInformationChange = (event: any) => {
        setAnyChangeMade(true);
        if (event.nativeEvent.inputType === 'insertLineBreak') {
            setReasonDontHaveAllInformationValue(reasonDontHaveAllInformationValue);
        } else {
            setReasonDontHaveAllInformationValue(event.target.value);
        }
    };

    React.useEffect(() => {
        if (physicianReview) {
            physicianReview.isInclusionCriteriaMet = isMetValue;
            physicianReview.reasonInclusionCriteria = reasonInclusionCriteriaValue;
            physicianReview.isExclusionCriteriaPresent = isExclusionCriteriaValue;
            physicianReview.listExclusionCriteria = listExclusionCriteriaValue;
            physicianReview.isServiceAppropiate = isServiceAppropiateValue;
            physicianReview.reasonServiceAppropiate = reasonServiceAppropiateValue;
            physicianReview.haveAllInformation = haveAllInformationValue;
            physicianReview.reasonDontHaveAllInformation = reasonDontHaveAllInformationValue;
            physicianReview.isReviewed = isReviewedValue;
        }
    }, [
        isMetValue,
        reasonInclusionCriteriaValue,
        isExclusionCriteriaValue,
        listExclusionCriteriaValue,
        isServiceAppropiateValue,
        reasonServiceAppropiateValue,
        haveAllInformationValue,
        reasonDontHaveAllInformationValue,
        isReviewedValue,
        physicianReview
    ]);

    return (
        <Box
            sx={{
                margin: !isSmallerThanSmall ? '0 1.25rem 0.875rem' : '0 0 0.875rem 0',
                padding: !isSmallerThanSmall ? '0 0.625rem 0.625rem' : '0 0 0.625rem',
                background: 'white',
                borderRadius: !isSmallerThanSmall ? '0.625rem' : '0'
            }}>
            <Typography variant="h1" sx={isSmallerThanSmall ? { paddingLeft: '0.625rem' } : {}}>
                {label}
                <Typography sx={{ color: 'red', display: 'inline' }}>*</Typography>
            </Typography>
            <Typography
                variant="h6"
                sx={
                    isSmallerThanSmall
                        ? { color: t.palette.cardSubtitle.main, paddingLeft: '0.625rem' }
                        : { color: t.palette.cardSubtitle.main }
                }>
                Select Rationale
            </Typography>

            <Box sx={isSmallerThanSmall ? { padding: '0 0.625rem' } : {}}>
                <Box sx={firstHeaderRowStyle}>
                    <FormControlLabel
                        label="I have personally reviewed all attached medical policies and clinical information available for this case"
                        value={isReviewedValue}
                        componentsProps={{
                            typography: { fontSize: '0.875rem', fontWeight: '700' }
                        }}
                        control={
                            <Checkbox
                                checked={isReviewedValue}
                                sx={{ padding: '0 0.75rem' }}
                                onChange={(_: unknown, isChecked: boolean) => {
                                    setIsReviewedValue(isChecked);
                                    setAnyChangeMade(true);
                                }}
                            />
                        }
                    />
                </Box>
            </Box>

            <Box sx={isSmallerThanSmall ? { padding: '0 0.625rem' } : {}}>
                <Box sx={headerRowStyle}>
                    <Typography variant="h5">
                        Do you have enough policy required documentation to render a medical
                        necessity determination?{' '}
                        <Typography sx={{ fontStyle: 'italic' }}>
                            (If no clinicals, please select no and notate no clinical attached)
                        </Typography>
                    </Typography>
                </Box>
                <Box sx={radioRowStyle}>
                    <RadioGroup sx={{ flexDirection: 'row' }}>
                        <Box sx={borderRightStyle}>
                            <FormControlLabel
                                componentsProps={{ typography: { fontSize: '14px' } }}
                                label="Yes"
                                checked={isNewAllInformation ? false : haveAllInformationValue}
                                control={
                                    <Radio
                                        size="small"
                                        onChange={(_: unknown, isChecked: boolean) => {
                                            setHaveAllInformationValue(isChecked);
                                            setIsNewAllInformation(false);
                                            setAnyChangeMade(true);
                                        }}
                                    />
                                }
                            />
                        </Box>
                        <Box sx={{ paddingLeft: '9px' }}>
                            <FormControlLabel
                                componentsProps={{ typography: { fontSize: '14px' } }}
                                label="No"
                                checked={isNewAllInformation ? false : !haveAllInformationValue}
                                control={
                                    <Radio
                                        size="small"
                                        onChange={(_: unknown, isChecked: boolean) => {
                                            setHaveAllInformationValue(!isChecked);
                                            setIsNewAllInformation(false);
                                            setAnyChangeMade(true);
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </RadioGroup>
                </Box>
                <Box sx={grayedRowStyle}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                        <Typography sx={reasonTextStyle}>Explain Reasoning</Typography>
                        <TextareaAutosize
                            value={reasonDontHaveAllInformationValue}
                            minRows={2}
                            style={{
                                width: isSmallerThanSmall
                                    ? '75%'
                                    : isSmallerThanMedium
                                    ? '80%'
                                    : '100%',
                                minWidth: '50%',
                                maxWidth: isSmallerThanSmall ? '75%' : '100%',
                                minHeight: '20px'
                            }}
                            onChange={(v) => handleReasonDontHaveAllInformationChange(v)}
                        />
                    </Box>
                </Box>

                <Box sx={headerRowStyle}>
                    <Typography variant="h5">
                        Medical Policy: Is the Inclusion Criteria Met? (Section I.)
                    </Typography>
                </Box>
                <Box sx={radioRowStyle}>
                    <RadioGroup sx={{ flexDirection: 'row' }}>
                        <Box sx={borderRightStyle}>
                            <FormControlLabel
                                componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                label="Met"
                                checked={isNewInclusionCriteria ? false : isMetValue}
                                control={
                                    <Radio
                                        size="small"
                                        onChange={(_: unknown, isChecked: boolean) => {
                                            setIsMetValue(isChecked);
                                            setIsNewInclusionCriteria(false);
                                            setAnyChangeMade(true);
                                        }}
                                    />
                                }
                            />
                        </Box>
                        <Box sx={{ paddingLeft: '0.563rem' }}>
                            <FormControlLabel
                                componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                label="Not Met"
                                checked={isNewInclusionCriteria ? false : !isMetValue}
                                control={
                                    <Radio
                                        size="small"
                                        onChange={(_: unknown, isChecked: boolean) => {
                                            setIsMetValue(!isChecked);
                                            setIsNewInclusionCriteria(false);
                                            setAnyChangeMade(true);
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </RadioGroup>
                </Box>
                <Box sx={grayedRowStyle}>
                    <FlexCenterBox>
                        <Typography sx={reasonTextStyle}>Explain which criteria</Typography>
                        <TextareaAutosize
                            value={reasonInclusionCriteriaValue}
                            minRows={2}
                            style={{
                                width: isSmallerThanSmall
                                    ? '75%'
                                    : isSmallerThanMedium
                                    ? '80%'
                                    : '100%',
                                minWidth: '50%',
                                maxWidth: isSmallerThanSmall ? '75%' : '100%',
                                minHeight: '1.25rem'
                            }}
                            onChange={(v) => handleReasonInclusionCriteriaChange(v)}
                        />
                    </FlexCenterBox>
                </Box>

                <Box sx={headerRowStyle}>
                    <Typography variant="h5">
                        Medical Policy: Are any Exclusion Criteria present? (Section II.){' '}
                    </Typography>
                </Box>
                <Box sx={radioRowStyle}>
                    <RadioGroup sx={{ flexDirection: 'row' }}>
                        <Box sx={borderRightStyle}>
                            <FormControlLabel
                                componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                label="No"
                                checked={isNewExclusionCriteria ? false : !isExclusionCriteriaValue}
                                control={
                                    <Radio
                                        size="small"
                                        onChange={(_: unknown, isChecked: boolean) => {
                                            setIsExclusionCriteriaValue(!isChecked);
                                            setIsNewExclusionCriteria(false);
                                            setAnyChangeMade(true);
                                        }}
                                    />
                                }
                            />
                        </Box>
                        <Box sx={{ paddingLeft: '0.563rem' }}>
                            <FormControlLabel
                                componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                label="Yes"
                                checked={isNewExclusionCriteria ? false : isExclusionCriteriaValue}
                                control={
                                    <Radio
                                        size="small"
                                        onChange={(_: unknown, isChecked: boolean) => {
                                            setIsExclusionCriteriaValue(isChecked);
                                            setIsNewExclusionCriteria(false);
                                            setAnyChangeMade(true);
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </RadioGroup>
                </Box>
                <Box sx={grayedRowStyle}>
                    <FlexCenterBox>
                        <Typography sx={reasonTextStyle}>Explain which criteria</Typography>
                        <TextareaAutosize
                            value={listExclusionCriteriaValue}
                            minRows={2}
                            style={{
                                width: isSmallerThanSmall
                                    ? '75%'
                                    : isSmallerThanMedium
                                    ? '80%'
                                    : '100%',
                                minWidth: '50%',
                                maxWidth: isSmallerThanSmall ? '75%' : '100%',
                                minHeight: '1.25rem'
                            }}
                            onChange={(v) => handleListExclusionCriteriaChange(v)}
                        />
                    </FlexCenterBox>
                </Box>

                <Box sx={headerRowStyleFlex}>
                    <Typography variant="h5">
                        Is the Site of Service requested medically necessary and appropriate for the
                        requested procedures?
                    </Typography>
                </Box>
                <Box sx={radioRowStyle}>
                    <RadioGroup sx={{ flexDirection: 'row' }}>
                        <Box sx={borderRightStyle}>
                            <FormControlLabel
                                componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                label="Yes"
                                checked={isNewServiceAppropiate ? false : isServiceAppropiateValue}
                                control={
                                    <Radio
                                        size="small"
                                        onChange={(_: unknown, isChecked: boolean) => {
                                            setIsServiceAppropiateValue(isChecked);
                                            setIsNewServiceAppropiate(false);
                                            setAnyChangeMade(true);
                                        }}
                                    />
                                }
                            />
                        </Box>
                        <Box sx={{ paddingLeft: '0.563rem' }}>
                            <FormControlLabel
                                componentsProps={{ typography: { fontSize: '0.875rem' } }}
                                label="No"
                                checked={isNewServiceAppropiate ? false : !isServiceAppropiateValue}
                                control={
                                    <Radio
                                        size="small"
                                        onChange={(_: unknown, isChecked: boolean) => {
                                            setIsServiceAppropiateValue(!isChecked);
                                            setIsNewServiceAppropiate(false);
                                            setAnyChangeMade(true);
                                        }}
                                    />
                                }
                            />
                        </Box>
                    </RadioGroup>
                </Box>
                <Box sx={lastGrayedRowStyle}>
                    <FlexCenterBox>
                        <Typography sx={reasonTextStyle}>
                            Explain comorbidity screen results
                        </Typography>
                        <TextareaAutosize
                            value={reasonServiceAppropiateValue}
                            minRows={2}
                            style={{
                                width: isSmallerThanSmall
                                    ? '75%'
                                    : isSmallerThanMedium
                                    ? '80%'
                                    : '100%',
                                minWidth: '50%',
                                maxWidth: isSmallerThanSmall ? '75%' : '100%',
                                minHeight: '1.25rem'
                            }}
                            onChange={(v) => handleReasonServiceAppropiateChange(v)}
                        />
                    </FlexCenterBox>
                </Box>
            </Box>
        </Box>
    );
};

export default PhysicianReviewFirstPart;
